import React, { useEffect, useState } from "react";
import { Button, Table, Card, Spinner, Alert, Badge, Tabs, Tab, Row, Col } from "react-bootstrap";
import axios from "axios";

const ImportStep2 = ({
  uploadedFile,
  columnMapping,
  validationResult,
  setValidationResult,
  onBack,
  onNext,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!validationResult) {
      validateData();
    }
  }, []);

  const validateData = async () => {
    setLoading(true);
    setError("");
    const formData = new FormData();
    formData.append("file", uploadedFile);
    formData.append("mapping", JSON.stringify(columnMapping));

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/exhibitors/import/validate`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (res.data.status) {
        setValidationResult(res.data.data);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Error validating data.");
    } finally {
      setLoading(false);
    }
  };

  if (loading || !validationResult) {
    return (
      <Card className="shadow-sm text-center py-5">
        <Card.Body>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Validating rows...</p>
        </Card.Body>
      </Card>
    );
  }

  const { summary, rows } = validationResult;
  const filteredRows = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  const getRowClass = (status) => {
    if (status === "ready") return "table-success";
    if (status === "error") return "table-danger";
    if (status === "duplicate") return "table-warning";
    return "";
  };

  const handleDownloadReport = (statusFilter, fileName) => {
    const rowsToDownload = rows.filter((r) => r.status === statusFilter);
    if (rowsToDownload.length === 0) return;

    const headers = "Row Number,Company Name,Contact Person,Mobile No.,Email,Stall Size,Stall No.,Reason\n";
    const csvContent = rowsToDownload.map(r => 
      `"${r.rowNumber}","${r.companyName}","${r.contactPerson}","${r.mobileNo}","${r.email}","${r.stallSize}","${r.stallNo}","${r.reason}"`
    ).join("\n");

    const blob = new Blob([headers + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <h5 className="mb-4">Step 2: Validation Report</h5>

        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="mb-4">
          <Col md={4}>
            <Card className="bg-light text-center border-success">
              <Card.Body>
                <h3 className="text-success">{summary.ready}</h3>
                <p className="mb-0 text-muted">Ready to Import</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="bg-light text-center border-warning">
              <Card.Body>
                <h3 className="text-warning">{summary.duplicates}</h3>
                <p className="mb-0 text-muted">Duplicates</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="bg-light text-center border-danger">
              <Card.Body>
                <h3 className="text-danger">{summary.errors}</h3>
                <p className="mb-0 text-muted">Errors</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="d-flex gap-2 mb-4 justify-content-end">
           <Button 
             variant="outline-warning" 
             size="sm" 
             onClick={() => handleDownloadReport("duplicate", "duplicate_report.csv")}
             disabled={summary.duplicates === 0}
           >
             Download Duplicate Report
           </Button>
           <Button 
             variant="outline-danger" 
             size="sm" 
             onClick={() => handleDownloadReport("error", "error_report.csv")}
             disabled={summary.errors === 0}
           >
             Download Fail Report
           </Button>
        </div>

        <Tabs activeKey={filter} onSelect={(k) => setFilter(k)} className="mb-3">
          <Tab eventKey="all" title={`All (${summary.total})`} />
          <Tab eventKey="ready" title={`Ready (${summary.ready})`} />
          <Tab eventKey="duplicate" title={`Duplicates (${summary.duplicates})`} />
          <Tab eventKey="error" title={`Errors (${summary.errors})`} />
        </Tabs>

        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
          <Table responsive bordered hover size="sm">
            <thead className="bg-light" style={{ position: "sticky", top: 0 }}>
              <tr>
                <th>#</th>
                <th>Company Name</th>
                <th>Contact Person</th>
                <th>Mobile No.</th>
                <th>Stall Size</th>
                <th>Status</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => (
                <tr key={idx} className={getRowClass(row.status)}>
                  <td>{row.rowNumber}</td>
                  <td>{row.companyName}</td>
                  <td>{row.contactPerson}</td>
                  <td>{row.mobileNo}</td>
                  <td>{row.stallSize}</td>
                  <td>
                    <Badge
                      bg={
                        row.status === "ready"
                          ? "success"
                          : row.status === "error"
                          ? "danger"
                          : "warning"
                      }
                      text={row.status === "duplicate" ? "dark" : "white"}
                    >
                      {row.status.toUpperCase()}
                    </Badge>
                  </td>
                  <td>{row.reason}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-muted">
                    No rows found for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
          <Button variant="primary" onClick={onNext} disabled={summary.ready === 0}>
            Proceed to Import
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ImportStep2;
